import { FC, Fragment } from 'react'

import { AssignmentRendererProps } from './AssignmentRenderer.types'
import {
  DetailSection,
  DetailField,
  DetailBooleanField,
  DetailCommentBlock,
  UploadedFilesSection,
  RendererQueryState
} from '../common'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'
import { useGetAssignmentAuthoritiesDocumentsDetails } from 'dataQueries/assignmentDetails.query'
import { useTranslation } from 'react-i18next'
import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'

const T_PREFIX = 'gallery:assignment_details.authorities_docs'

export const AuthoritiesDocumentsDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation(['gallery'])
  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const { data, isLoading, isError, refetch } = useGetAssignmentAuthoritiesDocumentsDetails(assignment.id, workspaceId)

  return (
    <RendererQueryState assignmentId={assignment.id} isLoading={isLoading} isError={isError} onRetry={refetch}>

      {!!data?.landLordInformation &&
        <DetailSection title={t(`${T_PREFIX}.landlord.title`)}>
          <DetailField
            label={t(`${T_PREFIX}.landlord.first_name`)}
            value={data.landLordInformation.firstName}
          />

          <DetailField
            label={t(`${T_PREFIX}.landlord.last_name`)}
            value={data.landLordInformation.lastName}
          />

          <DetailField
            label={t(`${T_PREFIX}.landlord.birth_date`)}
            value={data.landLordInformation.dateOfBirth}
          />
        </DetailSection>
      }

      {data?.searchLandRegistrationDetails &&
        <DetailSection title={t(`${T_PREFIX}.data_source_backbone`)}>
          <DetailBooleanField
            value={true}
          />
        </DetailSection>
      }

      {data?.landRegistrationSheets?.map((sheet, index) => (
        <Fragment key={index}>
          <DetailSection title={t(`${T_PREFIX}.land_register_sheet`)}>
            <DetailField
              label={t(`${T_PREFIX}.title_number`)}
              value={sheet.name}
            />

            <DetailField
              label={t(`${T_PREFIX}.cadastral_block`)}
              value={sheet.district}
            />
          </DetailSection>

          {sheet.floors.map((floor, floorIndex) => (
            <DetailSection
              key={floorIndex}
              title={`${t(`${T_PREFIX}.parcel_identification`)} #${floorIndex + 1}`}
            >
              <DetailField
                label={t(`${T_PREFIX}.cadastral_block`)}
                value={floor.hallway}
              />

              <DetailField
                label={t(`${T_PREFIX}.parcel`)}
                value={floor.parcels.map(p => p.number).join(', ')}
              />
            </DetailSection>
          ))}
        </Fragment>
      ))}

      {!!data?.comment &&
        <DetailSection title={t(`${T_PREFIX}.comments`)}>
          <DetailCommentBlock>{data.comment}</DetailCommentBlock>
        </DetailSection>
      }

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

      <UploadedFilesSection assignmentId={assignment.id} />

    </RendererQueryState>
  )
}
